var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { instanceToPlain, plainToClass } from 'class-transformer';
import queries from 'constants/queries';
import { PatientJourney } from 'models';
import PatientJourneyStep from 'models/entities/PatientJourneyStep';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ApiService from './ApiService';
var EMPTY_FILTERS = {};
export var existingPatientJourneyId = function (error) {
    if (!error || typeof error !== 'object')
        return undefined;
    var description = error.description;
    if (!Array.isArray(description))
        return undefined;
    var match = description.find(function (item) {
        if (!item || typeof item !== 'object')
            return false;
        return item.patient_journey_id != null;
    });
    var id = Number(match === null || match === void 0 ? void 0 : match.patient_journey_id);
    return Number.isFinite(id) && id > 0 ? id : undefined;
};
var PatientJourneyService = /** @class */ (function (_super) {
    __extends(PatientJourneyService, _super);
    function PatientJourneyService() {
        var _this = _super.call(this) || this;
        _this.root = 'patient_journeys';
        _this.successMessage = '';
        return _this;
    }
    PatientJourneyService.prototype.usePatientJourneys = function (filters, enabled) {
        var _this = this;
        if (filters === void 0) { filters = EMPTY_FILTERS; }
        if (enabled === void 0) { enabled = true; }
        var params = {};
        if (filters.patient_id != null) {
            params.patient_id = filters.patient_id;
        }
        if (filters.clinic_id != null) {
            params.clinic_id = filters.clinic_id;
        }
        if (filters.patient_name) {
            params.patient_name = filters.patient_name;
        }
        if (filters.clinic_name) {
            params.clinic_name = filters.clinic_name;
        }
        if (filters.patient_phone_number) {
            params.patient_phone_number = filters.patient_phone_number;
        }
        return useQuery([queries.patientJourneys, filters], function () {
            return _this.get(_this.root, params);
        }, {
            enabled: enabled,
            retry: false,
            select: function (_a) {
                var data = _a.data;
                return plainToClass(PatientJourney, (data || []));
            }
        });
    };
    PatientJourneyService.prototype.useStationQueue = function (clinicId, queueKind, enabled) {
        var _this = this;
        if (enabled === void 0) { enabled = true; }
        return useQuery([queries.patientJourneyQueue, clinicId, queueKind], function () {
            return _this.get("".concat(_this.root, "/queue"), {
                clinic_id: clinicId,
                queue_kind: queueKind
            });
        }, {
            enabled: enabled && clinicId != null,
            retry: false,
            refetchInterval: 30000,
            select: function (_a) {
                var data = _a.data;
                return plainToClass(PatientJourneyStep, (data || []));
            }
        });
    };
    PatientJourneyService.prototype.usePatientJourney = function (id, enabled) {
        var _this = this;
        if (enabled === void 0) { enabled = true; }
        return useQuery([queries.patientJourney, id], function () {
            return _this.get("".concat(_this.root, "/").concat(id));
        }, {
            enabled: enabled && !!id,
            retry: false,
            select: function (_a) {
                var data = _a.data;
                return plainToClass(PatientJourney, data);
            }
        });
    };
    PatientJourneyService.prototype.useCreatePatientJourney = function () {
        var _this = this;
        var queryClient = useQueryClient();
        return useMutation(function (_a) {
            var patientJourney = _a.patientJourney, successMessage = _a.successMessage;
            _this.successMessage = successMessage;
            var plain = instanceToPlain(patientJourney);
            delete plain.id;
            delete plain.patient_journey_steps;
            delete plain.clinic;
            delete plain.current_state;
            return _this.post(_this.root, {
                data: { patient_journey: plain }
            });
        }, {
            retry: false,
            onSuccess: function () {
                queryClient.invalidateQueries(queries.patientJourneys);
                _this.dipatachCreationSuccess({ successMessage: _this.successMessage });
            }
        });
    };
    PatientJourneyService.prototype.useUpdatePatientJourney = function () {
        var _this = this;
        var queryClient = useQueryClient();
        return useMutation(function (_a) {
            var patientJourney = _a.patientJourney, successMessage = _a.successMessage;
            _this.successMessage = successMessage;
            return _this.put("".concat(_this.root, "/").concat(patientJourney.getId()), {
                data: { patient_journey: instanceToPlain(patientJourney) }
            });
        }, {
            retry: false,
            onSuccess: function (_response, variables) {
                _this.invalidate(queryClient, variables.patientJourney.getId());
            }
        });
    };
    PatientJourneyService.prototype.useAppendStep = function () {
        var _this = this;
        var queryClient = useQueryClient();
        return useMutation(function (_a) {
            var patientJourneyId = _a.patientJourneyId, stepableType = _a.stepableType, stepableId = _a.stepableId, stepKind = _a.stepKind, position = _a.position, notes = _a.notes, successMessage = _a.successMessage;
            _this.successMessage = successMessage;
            return _this.post("".concat(_this.root, "/").concat(patientJourneyId, "/append_step"), {
                data: {
                    stepable_type: stepableType,
                    stepable_id: stepableId,
                    step_kind: stepKind,
                    position: position,
                    notes: notes
                }
            });
        }, {
            retry: false,
            onSuccess: function (_response, variables) {
                _this.invalidate(queryClient, variables.patientJourneyId);
            }
        });
    };
    PatientJourneyService.prototype.useTransition = function () {
        var _this = this;
        var queryClient = useQueryClient();
        return useMutation(function (_a) {
            var patientJourneyId = _a.patientJourneyId, toState = _a.toState, metadata = _a.metadata, successMessage = _a.successMessage;
            _this.successMessage = successMessage;
            return _this.put("".concat(_this.root, "/").concat(patientJourneyId, "/transition"), {
                data: { to_state: toState, metadata: metadata || {} }
            });
        }, {
            retry: false,
            onSuccess: function (_response, variables) {
                _this.invalidate(queryClient, variables.patientJourneyId);
            }
        });
    };
    PatientJourneyService.prototype.useTransitionStep = function () {
        var _this = this;
        var queryClient = useQueryClient();
        return useMutation(function (_a) {
            var patientJourneyId = _a.patientJourneyId, patientJourneyStepId = _a.patientJourneyStepId, toState = _a.toState, metadata = _a.metadata, successMessage = _a.successMessage;
            _this.successMessage = successMessage;
            return _this.put("".concat(_this.root, "/").concat(patientJourneyId, "/transition_step"), {
                data: {
                    patient_journey_step_id: patientJourneyStepId,
                    to_state: toState,
                    metadata: metadata || {}
                }
            });
        }, {
            retry: false,
            onSuccess: function (_response, variables) {
                _this.invalidate(queryClient, variables.patientJourneyId);
            }
        });
    };
    PatientJourneyService.prototype.invalidate = function (queryClient, id) {
        queryClient.invalidateQueries(queries.patientJourneys);
        queryClient.invalidateQueries(queries.patientJourneyQueue);
        queryClient.invalidateQueries([queries.patientJourney, id]);
        this.dipatachCreationSuccess({ successMessage: this.successMessage });
    };
    PatientJourneyService.getInstance = function () {
        if (!PatientJourneyService.instance) {
            PatientJourneyService.instance = new PatientJourneyService();
        }
        return PatientJourneyService.instance;
    };
    return PatientJourneyService;
}(ApiService));
export default PatientJourneyService;
